// Configuration métier partagée : prestations, champs, statuts, helpers numériques.

export const STATUS_META = {
  brouillon: { label: "Brouillon", badge: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  a_verifier: { label: "À vérifier", badge: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
  a_completer: { label: "À compléter", badge: "bg-orange-50 text-orange-700 border-orange-200", dot: "bg-orange-500" },
  validee: { label: "Validée", badge: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
};

export const SYNC_META = {
  local: { label: "Enregistré sur cet appareil", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  syncing: { label: "Synchronisation…", cls: "bg-sky-50 text-sky-700 border-sky-200 animate-pulse" },
  synced: { label: "Synchronisé", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  offline: { label: "Hors ligne — en attente", cls: "bg-slate-100 text-slate-700 border-slate-200" },
  conflict: { label: "Conflit détecté", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  error: { label: "Échec de synchronisation", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const F = (key, label, type, extra = {}) => ({ key, label, type, ...extra });

export const PRESTATIONS = {
  vitres: {
    id: "vitres", name: "Vitres", icon: "PanelTop", unit: "m²", multiLine: true,
    lineLabel: "vitrage",
    fields: [
      F("type_vitrage", "Type de vitrage", "select", { options: ["Simple", "Double"] }),
      F("quantite", "Nombre d'éléments identiques", "number", { integer: true }),
      F("largeur", "Largeur", "number", { unit: "m" }),
      F("hauteur", "Hauteur", "number", { unit: "m" }),
      F("faces", "Faces à nettoyer", "select", { options: [{ v: 1, l: "1 face" }, { v: 2, l: "2 faces" }] }),
      F("hauteur_acces", "Hauteur d'accès", "number", { unit: "m" }),
      F("grilles", "Grilles ou obstacles", "boolean"),
      F("etat_salissure", "État de salissure", "select", { options: ["Faible", "Moyen", "Fort"] }),
    ],
    compute: "surface_vitres",
  },
  gouttieres: {
    id: "gouttieres", name: "Gouttières", icon: "Pipette", unit: "ml", multiLine: true, lineLabel: "tronçon",
    fields: [
      F("longueur", "Longueur", "number", { unit: "ml" }),
      F("hauteur_intervention", "Hauteur d'intervention", "number", { unit: "m" }),
      F("acces_toiture", "Accès toiture", "select", { options: ["Simple", "Complexe"] }),
      F("crochets", "Présence de crochets", "select", { options: ["Oui", "Non"] }),
      F("materiau", "Matériau (si connu)", "text"),
      F("encombrement", "Niveau d'encombrement", "select", { options: ["Faible", "Moyen", "Fort"] }),
      F("etat", "État apparent", "text"),
      F("obstacles_acces", "Obstacles et accès", "text"),
    ],
  },
  depoussierage: {
    id: "depoussierage", name: "Dépoussiérage", icon: "Wind", unit: "m²/ml/U", multiLine: true, lineLabel: "élément",
    fields: [
      F("elements", "Éléments concernés", "text", { placeholder: "poutres, tuyauteries, structures…" }),
      F("quantite", "Quantité", "number"),
      F("unite", "Unité", "select", { options: ["m²", "ml", "U"] }),
      F("hauteur_intervention", "Hauteur d'intervention", "number", { unit: "m" }),
      F("salissure", "Niveau de salissure", "select", { options: ["Faible", "Moyen", "Fort"] }),
      F("fragilite", "Fragilité / équipements sensibles", "select", { options: ["Oui", "Non"] }),
    ],
  },
  sols_facades: {
    id: "sols_facades", name: "Sols / Façades / Toitures (ancien relevé)", legacy: true, icon: "Layers", unit: "m²", multiLine: true, lineLabel: "surface",
    fields: [
      F("categorie", "Catégorie", "select", { options: ["Sols", "Façades", "Toitures"] }),
      F("longueur", "Longueur", "number", { unit: "m" }),
      F("largeur", "Largeur", "number", { unit: "m" }),
      F("surface", "Surface totale (si connue)", "number", { unit: "m²" }),
      F("mesure", "Type de valeur", "select", { options: ["mesurée", "estimée"] }),
      F("nature_support", "Nature du support", "text"),
      F("etat_salissure", "État apparent et salissures", "text"),
      F("hauteur_pente", "Hauteur ou pente (si pertinent)", "number", { unit: "m" }),
      F("contraintes_acces", "Contraintes d'accès", "text"),
    ],
    compute: "surface_sols",
  },
  facades: { id: "facades", name: "Façades", icon: "Building2", unit: "m²", surface: true, fields: [] },
  sols: { id: "sols", name: "Sols", icon: "Grid3x3", unit: "m²", surface: true, fields: [] },
  toitures: { id: "toitures", name: "Toitures", icon: "Home", unit: "m²", surface: true, fields: [] },
  balisage: {
    id: "balisage", name: "Balisage & équipements", icon: "ShieldAlert", unit: "m/U", multiLine: true, lineLabel: "équipement",
    fields: [
      F("type", "Type", "select", { options: ["Plots solaires", "Nez de marches", "Marqueurs photoluminescents", "Équipements podotactiles", "Autres"] }),
      F("quantite", "Quantité ou longueur", "number"),
      F("unite", "Unité", "select", { options: ["m", "U"] }),
      F("nature_support", "Nature du support", "text"),
      F("implantation", "Implantation souhaitée", "text"),
      F("etat_support", "État du support", "select", { options: ["Sain", "Abîmé"] }),
    ],
  },
  autre: {
    id: "autre", name: "Autre prestation", icon: "SquarePen", unit: "libre", multiLine: true, lineLabel: "élément",
    fields: [
      F("libelle", "Intitulé de la prestation", "text", { placeholder: "Décrivez la prestation à relever" }),
      F("quantite", "Quantité", "number"),
      F("unite", "Unité de mesure", "select", { options: ["U", "m²", "ml", "m", "kg", "h", "Forfait"] }),
      F("hauteur_intervention", "Hauteur d'intervention", "number", { unit: "m" }),
      F("nature_support", "Nature du support", "text"),
      F("observations_ligne", "Observations", "text"),
    ],
  },
};

export const PRESTATION_LIST = Object.values(PRESTATIONS);

export const ACCESS_MEANS = [
  { v: "sol", l: "Depuis le sol" }, { v: "perche", l: "Perche" }, { v: "nacelle", l: "Nacelle" },
  { v: "echafaudage", l: "Échafaudage" }, { v: "cordes", l: "Cordes" }, { v: "autres", l: "Autres" },
];

// ---- Numériques FR ----
export function parseFr(str) {
  if (str === null || str === undefined) return null;
  const s = String(str).trim();
  if (s === "") return null;
  const n = Number(s.replace(",", "."));
  return Number.isNaN(n) ? null : n;
}

export function toDisplay(num) {
  if (num === null || num === undefined || num === "") return "";
  return String(num).replace(".", ",");
}

export function fmtNum(num, decimals = 2) {
  if (num === null || num === undefined || Number.isNaN(num)) return "—";
  return Number(num).toLocaleString("fr-FR", { maximumFractionDigits: decimals });
}

export function surfaceVitres(ligne) {
  const q = parseFr(ligne.quantite), l = parseFr(ligne.largeur), h = parseFr(ligne.hauteur), f = parseFr(ligne.faces);
  if ([q, l, h, f].some((x) => x === null)) return null;
  return q * l * h * f;
}

// Sols/façades/toitures : longueur × largeur si présents, sinon surface totale saisie.
export function surfaceSols(ligne) {
  const lo = parseFr(ligne.longueur), la = parseFr(ligne.largeur);
  if (lo !== null && la !== null) return lo * la;
  return parseFr(ligne.surface);
}

export function ligneSurface(compute, ligne) {
  if (compute === "surface_vitres") return surfaceVitres(ligne);
  if (compute === "surface_sols") return surfaceSols(ligne);
  return null;
}

// Complétude d'une visite pour la soumission
export function missingPoints(visit) {
  const missing = [];
  if (!visit.prestations?.length) missing.push("Aucune prestation sélectionnée");
  if (!visit.zones?.length) missing.push("Aucune zone / relevé ajouté");
  (visit.zones || []).forEach((z) => {
    if (!z.name?.trim()) missing.push("Une zone n'a pas de nom");
    if (!z.lignes?.length) missing.push(`Zone « ${z.name || "?"} » sans relevé`);
    if (z.prestation === "vitres") {
      (z.lignes || []).forEach((l, i) => {
        if (surfaceVitres(l) === null) missing.push(`Vitres — ${z.name} (ligne ${i + 1}) : dimension manquante`);
      });
    }
    if (z.prestation === "sols_facades") {
      (z.lignes || []).forEach((l, i) => {
        if (surfaceSols(l) === null) missing.push(`Sols/Façades — ${z.name} (ligne ${i + 1}) : surface ou dimensions manquantes`);
      });
    }
  });
  const c = visit.constraints || {};
  if (!c.point_eau) missing.push("Point d'eau non renseigné (oui / non / à confirmer)");
  if (!c.electricite) missing.push("Électricité non renseignée (oui / non / à confirmer)");
  return missing;
}

export function fmtDate(d) {
  if (!d) return "—";
  try {
    let dt;
    if (typeof d === "string" && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split("-").map(Number);
      dt = new Date(y, m - 1, day); // date seule → local, évite le décalage UTC (‑1 jour)
    } else {
      dt = new Date(d);
    }
    return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  } catch { return d; }
}

// Date du jour au format ISO "yyyy-MM-dd" en heure LOCALE (pas UTC).
export function todayIso() {
  const n = new Date();
  const m = String(n.getMonth() + 1).padStart(2, "0");
  const d = String(n.getDate()).padStart(2, "0");
  return `${n.getFullYear()}-${m}-${d}`;
}

// Date + heure locale (ex. "21 sept. 2026, 14:05").
export function fmtDateTime(d) {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return d; }
}
